"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Mail, Clock, Send, CircleCheck, CircleAlert, Loader, MessageSquare } from "lucide-react";
import { Link } from "@/components/common/Link";
const contactDetails = [{
  icon: Phone,
  title: "Telefono",
  value: "+39 049 86 40 662",
  link: "tel:+390498640662"
}, {
  icon: MessageSquare,
  title: "WhatsApp",
  value: "+39 345 84 74 911",
  link: "https://wa.me/393458474911"
}, {
  icon: Mail,
  title: "Informazioni Generali",
  value: "info@worldimmigration.it",
  link: "mailto:info@worldimmigration.it"
}, {
  icon: Mail,
  title: "Servizi Aziende",
  value: "aziende@worldimmigration.it",
  link: "mailto:aziende@worldimmigration.it"
}, {
  icon: Clock,
  title: "Orari di Ufficio",
  value: "Su appuntamento",
  link: null
}, {
  icon: MapPin,
  title: "Sede Principale",
  value: "Via Lorenzo Ghiberti 21, 35134 Padova",
  link: null
}];
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 bg-white rounded-xl shadow-md p-8 border border-border">
        <div className="size-16 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mb-4 shadow-sm">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Messaggio Inviato!</h3>
        <p className="text-muted-foreground font-sans max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 shadow-sm">
          Invia un altro messaggio
        </Button>
      </div>;
  }
  const inputClasses = "border-x-0 border-t-0 border-b-2 border-primary/20 focus-visible:border-primary focus-visible:ring-0 bg-white rounded-md shadow-sm px-4 py-6 font-sans transition-colors";
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6 bg-white/50 p-1 rounded-xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="firstName" rules={{
          required: "Il nome è obbligatorio"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-foreground">Nome</FormLabel>
                <FormControl>
                  <Input placeholder="Mario" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="lastName" rules={{
          required: "Il cognome è obbligatorio"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-foreground">Cognome</FormLabel>
                <FormControl>
                  <Input placeholder="Rossi" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="email" rules={{
          required: "L'email è obbligatoria",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Inserisci un indirizzo email valido"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-foreground">Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="mario.rossi@esempio.it" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-foreground">Telefono (Opzionale)</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+39 333 1234567" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" rules={{
        required: "Il messaggio è obbligatorio",
        minLength: {
          value: 10,
          message: "Il messaggio deve contenere almeno 10 caratteri"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Come possiamo aiutarti?</FormLabel>
              <FormControl>
                <Textarea placeholder="Descrivi brevemente la tua richiesta..." className={`min-h-[150px] resize-y ${inputClasses}`} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3 shadow-sm">
            <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
            <p className="text-sm text-destructive font-medium">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-[#FFC829] text-primary hover:bg-[#FFC829]/90 shadow-md hover:shadow-lg transition-all duration-300 text-base px-8 py-6 h-auto font-default font-semibold rounded-lg">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Invio in corso...
            </> : <>
              Invia Richiesta
              <Send className="ml-2 size-5" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function RichiediAssistenza() {
  return <section id="richiedi-assistenza" className="py-16 md:py-24 bg-background relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-4">
            Richiedi Assistenza
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Compila il modulo sottostante o utilizza i seguenti recapiti diretti per ricevere una consulenza dedicata e professionale.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start max-w-6xl mx-auto">
          
          <div className="lg:col-span-7 xl:col-span-8 order-2 lg:order-1">
            <div className="bg-card/50 backdrop-blur-sm p-6 md:p-8 rounded-2xl shadow-lg border border-border/50">
              <h3 className="text-2xl font-default font-semibold text-foreground mb-6">
                Inviaci un messaggio
              </h3>
              <FormProvider formId="richiedi-assistenza-form" sectionName="richiedi-assistenza" defaultValues={{
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              message: ""
            }} submitText="Invia Richiesta" successMessage="Grazie per averci contattato. Il nostro team ti risponderà il prima possibile.">
                <ContactFormContent />
              </FormProvider>
            </div>
          </div>

          <div className="lg:col-span-5 xl:col-span-4 order-1 lg:order-2">
            <div className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground p-8 md:p-10 rounded-2xl shadow-2xl relative overflow-hidden h-full">
              <div className="absolute top-0 right-0 -mt-16 -mr-16 size-64 bg-secondary/10 rounded-lg blur-3xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 -mb-16 -ml-16 size-48 bg-secondary/10 rounded-lg blur-2xl pointer-events-none" />
              
              <div className="relative z-10">
                <h3 className="text-2xl font-default font-semibold mb-8 text-white">
                  Informazioni di Contatto
                </h3>
                
                <div className="space-y-8">
                  {contactDetails.map((detail, i) => <div key={i} data-index={i} className="flex items-start gap-4 group">
                      <div className="bg-secondary/20 p-3 rounded-xl text-secondary group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors duration-300 shadow-sm shrink-0">
                        <detail.icon className="size-6" />
                      </div>
                      <div>
                        <p className="text-sm font-sans text-primary-foreground/70 mb-1">
                          {detail.title}
                        </p>
                        {detail.link ? <Link to={detail.link} className="text-lg font-default font-medium text-white hover:text-secondary transition-colors">
                            {detail.value}
                          </Link> : <p className="text-lg font-default font-medium text-white">
                            {detail.value}
                          </p>}
                      </div>
                    </div>)}
                </div>

                <div className="mt-12 pt-8 border-t border-primary-foreground/20">
                  <p className="text-sm font-sans text-primary-foreground/80 leading-relaxed">
                    Il nostro servizio clienti è attivo durante gli orari di ufficio. Per urgenze fuori orario, ti preghiamo di inviare un'email con priorità alta.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}